import _ from 'lodash';

import { TAG_FILTER_MODE_DEFAULT, matchesTagFilter } from '@libs/NwUtils';

import { getBookLinkedWebsites } from './BookItem/BookItemUtils';

export const BOOKS_SORT_STORAGE_KEY = 'nw_config_books_sort';
export const BOOKS_SORT_OPTIONS = [
    { label: 'fast/published on top', value: 'published' },
    { label: 'mine on top', value: 'mine' },
];
export const BOOKS_CHARACTERISTIC_FILTERS = {
    ALL: 'all',
    PUBLISHED: 'published',
    FAST_PACKAGE: 'fast-package',
    COMP_CARD: 'comp-card',
    FAVOURITE: 'favourite',
};

/** Axios response or plain array from getPlaylists endpoints. */
export function coercePlaylistArray(raw) {
    if (raw == null) {
        return null;
    }
    if (Array.isArray(raw)) {
        return raw;
    }
    if (Array.isArray(raw?.data)) {
        return raw.data;
    }
    return null;
}

export function addMineSortToPlaylists(playlists, currentUserId) {
    if (!playlists?.length) {
        return [];
    }

    return playlists.map((playlist) => {
        let mineSort = 0;
        if (playlist.UserCreated === currentUserId) {
            mineSort += 5;
            if (playlist.PrivacyLevelName === 'User') {
                mineSort += 5;
            }
        }
        if (playlist.IsPublished) {
            mineSort += 2;
        }
        if (playlist.FastPackage) {
            mineSort += 3;
        }
        if (playlist.CompCard) {
            mineSort += 1;
        }
        // il favourite e' l'unico segnale scelto dall'utente stesso: pesa piu' dei flag
        // di agenzia, ma non quanto l'aver creato il book
        if (playlist.IsFavourite) {
            mineSort += 4;
        }

        return {
            ...playlist,
            mineSort,
        };
    });
}

export function filterPlaylistsByTags(playlists, filterTags, filterMode = TAG_FILTER_MODE_DEFAULT) {
    if (!playlists?.length || !filterTags?.length) {
        return playlists?.length ? [...playlists] : [];
    }

    return playlists.filter((book) => matchesTagFilter(book, filterTags, filterMode));
}

export function filterPlaylistsByOwner(playlists, currentUserId, onlyMine) {
    if (!playlists?.length) {
        return [];
    }
    if (!onlyMine) {
        return [...playlists];
    }

    return playlists.filter((book) => book.UserCreated === currentUserId);
}

export function isPlaylistPublished(book, websiteData) {
    return getBookLinkedWebsites(book, websiteData).length > 0;
}

export function filterPlaylistsByCharacteristic(playlists, characteristicFilter, websiteData) {
    if (!playlists?.length) {
        return [];
    }
    if (!characteristicFilter || characteristicFilter === BOOKS_CHARACTERISTIC_FILTERS.ALL) {
        return [...playlists];
    }

    return playlists.filter((book) => {
        if (characteristicFilter === BOOKS_CHARACTERISTIC_FILTERS.PUBLISHED) {
            return isPlaylistPublished(book, websiteData);
        }
        if (characteristicFilter === BOOKS_CHARACTERISTIC_FILTERS.FAST_PACKAGE) {
            return Boolean(book.FastPackage);
        }
        if (characteristicFilter === BOOKS_CHARACTERISTIC_FILTERS.COMP_CARD) {
            return Boolean(book.CompCard);
        }
        if (characteristicFilter === BOOKS_CHARACTERISTIC_FILTERS.FAVOURITE) {
            return Boolean(book.IsFavourite);
        }

        return true;
    });
}

export function filterPackagePlaylistsWithMedia(playlists) {
    if (!playlists?.length) {
        return [];
    }

    return playlists.filter((book) => book.DocumentPlaylists.length > 0);
}

export function sortPlaylistsForBooks(playlists, booksSort, useMineSort) {
    if (!playlists?.length) {
        return [];
    }

    if (booksSort === 'mine' && useMineSort) {
        return _.orderBy(playlists, ['mineSort', 'ID'], ['desc', 'desc']);
    }

    // I favourites stanno sopra a published e compcard, ma sotto al blocco fast package:
    // quello ha un ordine manuale suo (FastPackageOrder) che non va spezzato.
    return _.orderBy(
        playlists,
        ['FastPackage', 'FastPackageOrder', 'IsFavourite', 'IsPublished', 'CompCard', 'ID'],
        ['desc', 'asc', 'desc', 'desc', 'desc', 'desc']
    );
}

/** Playlists with FastPackage flag (same filter as ModelDetailsBooksCard fast-only list). */
export function filterFastPackagePlaylists(playlists) {
    if (!playlists?.length) {
        return [];
    }
    return playlists.filter((book) => book.FastPackage);
}

/**
 * Sort by FastPackageOrder when numeric, else Infinity; tie-break ID ascending.
 * Mutates order only via new array of shallow clones (input unchanged).
 */
export function sortFastPackagePlaylistsByOrderThenId(playlists) {
    const clones = playlists.map((b) => ({ ...b }));
    return _.orderBy(
        clones,
        [
            (b) =>
                b.FastPackageOrder != null &&
                b.FastPackageOrder !== '' &&
                Number.isFinite(Number(b.FastPackageOrder))
                    ? Number(b.FastPackageOrder)
                    : Infinity,
            'ID',
        ],
        ['asc', 'asc']
    );
}

/**
 * Fast-package playlists only, sorted, then FastPackageOrder 0…n−1 and _fastPackageOrderFromServer
 * (aligned with normalizedFastPackageBooks in ModelDetailsBooksCard).
 * Use options.alreadyFastFiltered when the response is already fast-package-only (e.g. getFastPackages).
 */
export function normalizeFastPackageBooks(playlists, options = {}) {
    const { alreadyFastFiltered = false } = options;
    const fastOnes = alreadyFastFiltered
        ? playlists?.length
            ? playlists
            : []
        : filterFastPackagePlaylists(playlists);
    if (!fastOnes.length) {
        return [];
    }
    const sorted = sortFastPackagePlaylistsByOrderThenId(fastOnes);
    return sorted.map((b, i) => {
        const hadServerFastPackageOrder =
            b.FastPackageOrder != null &&
            b.FastPackageOrder !== '' &&
            Number.isFinite(Number(b.FastPackageOrder));
        return {
            ...b,
            FastPackageOrder: i,
            _fastPackageOrderFromServer: hadServerFastPackageOrder,
        };
    });
}

/** Key for resetting drag state when server/filter-derived fast list changes. */
export function getFastPackageSourceKeyFromPlaylists(playlists) {
    const fastOnes = filterFastPackagePlaylists(playlists);
    return fastOnes
        .map((b) => {
            const raw =
                b.FastPackageOrder != null &&
                b.FastPackageOrder !== '' &&
                Number.isFinite(Number(b.FastPackageOrder))
                    ? Number(b.FastPackageOrder)
                    : 'x';
            return { id: b.ID, key: `${b.ID}:${raw}` };
        })
        .sort((a, b) => a.id - b.id)
        .map((x) => x.key)
        .join('|');
}
